import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * MultiSelectDropdown - Enterprise-grade multi-select filter control
 * Clean select-style UI (no checkboxes) with pills for selected values
 * Per Filter Doctrine: OR logic within dropdown (select ANY of the values)
 * 
 * Sizing normalized to match SelectField (Application Deadline)
 */
const MultiSelectDropdown = ({ 
  label,
  options,
  selectedValues = [],
  onChange,
  placeholder = 'Any',
  collapsed = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const toggleOption = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };
  
  const removeValue = (e, value) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== value));
  };
  
  const getLabel = (value) => {
    const opt = options.find(o => o.value === value);
    return opt?.label || value;
  };
  
  // If collapsed by default and never expanded, show expand link
  if (collapsed && !isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center gap-1 text-sm text-[#1a5d3a] hover:underline"
      >
        <span>+ {label}</span>
      </button>
    );
  }
  
  const hasSelections = selectedValues.length > 0;
  
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      
      {/* Dropdown trigger - normalized to match SelectField sizing */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          height: '48px',
          border: hasSelections ? '1px solid #1a5d3a' : '1px solid #E1E4E8',
          borderRadius: '12px',
          backgroundColor: 'white',
        }}
        className="w-full flex items-center justify-between gap-2 px-4 text-left text-sm focus:outline-none focus:ring-2 focus:ring-[#1a5d3a]/30 hover:border-gray-400"
      >
        <div className="flex-1 flex flex-wrap items-center gap-1 overflow-hidden">
          {hasSelections ? (
            selectedValues.length <= 2 ? (
              selectedValues.map(value => (
                <span 
                  key={value}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1a5d3a]/10 text-[#1a5d3a] rounded text-xs font-medium"
                >
                  {getLabel(value)}
                  <button
                    onClick={(e) => removeValue(e, value)}
                    className="hover:text-[#1a5d3a]/70 ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-gray-900 text-sm">
                {selectedValues.length} selected
              </span>
            )
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown 
          size={16} 
          className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Dropdown menu - clean select style */}
      {isOpen && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white shadow-lg overflow-auto"
          style={{
            border: '1px solid #E1E4E8',
            borderRadius: '12px',
            maxHeight: '280px',
          }}
        >
          <div className="p-1">
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOption(option.value)}
                  className="w-full px-4 py-2 text-left text-sm rounded-lg transition-colors"
                  style={{
                    color: isSelected ? 'white' : '#2A2F35',
                    backgroundColor: isSelected ? '#1a5d3a' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#F5F7F9';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
